import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid2,
  Modal,
  Alert,
  Stack,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../../../api/axiosConfig";

const cacheRtl = createCache({
  key: "adapter-date-fns-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

// --- Helpers -----------------------------------------------------------------
const INITIAL_FORM = Object.freeze({
  sleepTime: "",
  wakeupTime: "",
  heartRate1: "",
  heartRate2: "",
  breakfast: "",
  lunch: "",
  dinner: "",
  dailyWeight: "",
  waterDrink: "",
  painFeeling: false,
  painDescription: "",
  urinColor: "",
});

const toFloat = (v) => (v === "" || v == null ? 0 : parseFloat(v));
const toInt = (v) => (v === "" || v == null ? 0 : parseInt(v, 10));

/**
 * Adds 1 day to a given date string and returns ISO string.
 * This directly addresses the off-by-one issue you mentioned.
 */
const addOneDayToISO = (dateInput) => {
  // Try to parse common date shapes safely
  if (typeof dateInput === "string") {
    // yyyy-mm-dd
    const m1 = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m1) {
      const dt = new Date(Number(m1[1]), Number(m1[2]) - 1, Number(m1[3]));
      dt.setDate(dt.getDate() + 1);
      return dt.toISOString();
    }
    // yyyy/mm/dd (may be Jalali formatted as plain text)
    const m2 = dateInput.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
    if (m2) {
      const dt = new Date(Number(m2[1]), Number(m2[2]) - 1, Number(m2[3]));
      dt.setDate(dt.getDate() + 1);
      return dt.toISOString();
    }
  }
  const dt = new Date(dateInput);
  if (!Number.isNaN(dt)) {
    dt.setDate(dt.getDate() + 1);
    return dt.toISOString();
  }
  // Fallback: now
  return new Date().toISOString();
};

export default function PlayerDailyReportModal({ open, onClose, data }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "painFeeling" ? value === true || value === "true" : value,
    }));
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
    setFormData(INITIAL_FORM);
    setError("");
    setSuccess("");
    setLoading(false);
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        userId: data.userId,
        trainingCampId: data.campId,
        sleepTime: formData.sleepTime,
        wakeupTime: formData.wakeupTime,
        // FIX: add one day before converting to ISO
        reportDate: addOneDayToISO(data.date),
        heartRate1: toFloat(formData.heartRate1),
        heartRate2: toFloat(formData.heartRate2),
        breakfast: formData.breakfast.trim(),
        lunch: formData.lunch.trim(),
        dinner: formData.dinner.trim(),
        dailyWeight: toFloat(formData.dailyWeight),
        waterDrink: toFloat(formData.waterDrink),
        painFeeling: !!formData.painFeeling,
        painDescription: formData.painDescription.trim(),
        urinColor: toInt(formData.urinColor),
      };

      await axiosInstance.post(
        "/Training/api/Training/CreateAthleteDailyReport",
        payload,
      );

      setSuccess("گزارش روزانه با موفقیت ارسال شد.");
      handleClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "خطا در ارسال گزارش. لطفاً دوباره تلاش کنید.";
      setError(`خطا: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [data, formData, handleClose]);

  const StatusBanner = useMemo(
    () => (
      <Stack spacing={1} sx={{ my: 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}
        {loading ? <CircularProgress sx={{ alignSelf: "flex-start" }} /> : null}
      </Stack>
    ),
    [error, success, loading],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="daily-report-modal"
    >
      <CacheProvider value={cacheRtl}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            height: "90%",
            maxWidth: 800,
            bgcolor: "background.paper",
            border: "1px solid #ccc",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            overflowY: "auto",
            direction: "ltr",
          }}
        >
          {/* Header */}
          <Grid2 container justifyContent="space-between" alignItems="center">
            <Typography id="daily-report-modal" variant="h6">
              گزارش روزانه
            </Typography>
            <IconButton onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Grid2>

          {StatusBanner}

          {/* Sleep and Wake Time */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 2, mt: 1 }}
          >
            اطلاعات خواب و بیداری
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="time"
                  label="زمان بیداری"
                  InputLabelProps={{ shrink: true }}
                  name="wakeupTime"
                  value={formData.wakeupTime}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="time"
                  label="زمان خواب"
                  InputLabelProps={{ shrink: true }}
                  name="sleepTime"
                  value={formData.sleepTime}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
          </Grid2>

          {/* Heart Rate */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
          >
            ضربان قلب
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="number"
                  label="ضربان قلب زمان بیدار شدن"
                  name="heartRate1"
                  value={formData.heartRate1}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="number"
                  label="ضربان قلب زمان خوابیدن"
                  name="heartRate2"
                  value={formData.heartRate2}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </div>
            </Grid2>
          </Grid2>

          {/* Diet */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
          >
            رژیم غذایی
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  label="صبحانه"
                  name="breakfast"
                  value={formData.breakfast}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  label="نهار"
                  name="lunch"
                  value={formData.lunch}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  label="شام"
                  name="dinner"
                  value={formData.dinner}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
          </Grid2>

          {/* Other Metrics */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 2, mt: 4 }}
          >
            معیارهای دیگر
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="number"
                  label="وزن روزانه"
                  name="dailyWeight"
                  value={formData.dailyWeight}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: "0.1" }}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  type="number"
                  label="مقدار آب نوشیده شده"
                  name="waterDrink"
                  value={formData.waterDrink}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: "0.1" }}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="pain-feeling-label">حساسیت به درد</InputLabel>
                  <Select
                    labelId="pain-feeling-label"
                    name="painFeeling"
                    value={formData.painFeeling}
                    onChange={handleChange}
                    label="حساسیت به درد"
                  >
                    <MenuItem value={true}>بله</MenuItem>
                    <MenuItem value={false}>خیر</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <TextField
                  fullWidth
                  label="توضیحات درد"
                  name="painDescription"
                  value={formData.painDescription}
                  onChange={handleChange}
                />
              </div>
            </Grid2>
            <Grid2 size={6}>
              <div dir="rtl">
                <FormControl fullWidth>
                  <InputLabel id="urin-color-label">رنگ ادرار</InputLabel>
                  <Select
                    labelId="urin-color-label"
                    name="urinColor"
                    value={formData.urinColor}
                    onChange={handleChange}
                    label="رنگ ادرار"
                  >
                    <MenuItem value={1}>عادی</MenuItem>
                    <MenuItem value={2}>زرد</MenuItem>
                    <MenuItem value={3}>تیره</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Grid2>
          </Grid2>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              انصراف
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "در حال ارسال..." : "ارسال"}
            </Button>
          </Box>
        </Box>
      </CacheProvider>
    </Modal>
  );
}
